import { TAccessStatus } from '@/shared/models/access-status';
import { IDocDefinition } from '@/shared/models/doc-definition';
import { UploadedFile } from '@/shared/models/uploaded-file';
import {
  hasAllRequiredDocs,
  getUploadedRequiredDocTypes,
  getRequiredDocsTypes,
} from '@/shared/utils/hasAllRequiredDocs';

import { EnumCardItemStatus, ICardItem, TCardStatus } from '../types';

type TItemAction = (params: Record<string, string>) => void;

interface IGetItemsParams<T, TDocType> {
  entities: T[];
  docDefinitions: IDocDefinition<TDocType>[];
  getTitle: (entity: T) => string;
  getInternalLink?: (entity: T) => string;
  getItemAction?: (entity: T) => () => void;
}

const getItem = <
  TDocType extends string,
  T extends {
    docUploads: Partial<Record<TDocType, UploadedFile<TDocType>>>;
    accessStatus: TAccessStatus;
  },
>({
  entities,
  docDefinitions,
  getTitle,
  getInternalLink,
  getItemAction,
}: IGetItemsParams<T, TDocType>) => {
  let hasRejectedDoc = false;
  const items = entities.map((entity): ICardItem => {
    const uploadedDocTypes = Object.keys(entity.docUploads) as TDocType[];
    const requiredDocTypes = getRequiredDocsTypes<TDocType>(docDefinitions);
    const hasAllDocs = hasAllRequiredDocs(entity.docUploads, docDefinitions);
    const uploadedRequiredDocTypes = getUploadedRequiredDocTypes<TDocType>(
      uploadedDocTypes,
      requiredDocTypes,
    );

    return {
      title: getTitle(entity),
      internalLink: getInternalLink?.(entity),
      action: getItemAction?.(entity),
      ...(() => {
        if (!hasAllDocs) {
          return {
            status: EnumCardItemStatus.addDocument,
            tipIntent: 'danger',
          };
        } else if (
          uploadedRequiredDocTypes.some(
            docType => entity.docUploads[docType]?.status === 'rejected',
          )
        ) {
          hasRejectedDoc = true;

          return {
            status: EnumCardItemStatus.documentsError,
            tipIntent: 'danger',
          };
        } else if (
          uploadedRequiredDocTypes.some(docType => entity.docUploads[docType]?.status === 'expired')
        ) {
          hasRejectedDoc = true;

          return {
            status: EnumCardItemStatus.documentsExpired,
            tipIntent: 'danger',
          };
        } else if (
          hasAllDocs &&
          uploadedRequiredDocTypes.some(docType => entity.docUploads[docType]?.status === 'pending')
        ) {
          return {
            status: EnumCardItemStatus.inReview,
            tipIntent: 'info',
          };
        } else if (
          uploadedRequiredDocTypes.every(
            docType => entity.docUploads[docType]?.status === 'approved',
          ) &&
          entity.accessStatus !== 'active'
        ) {
          return {
            status: EnumCardItemStatus.waitingForApproval,
            tipIntent: 'info',
          };
        } else if (entity.accessStatus === 'active') {
          return {
            status: EnumCardItemStatus.approved,
            tipIntent: 'success',
          };
        } else if (entity.accessStatus === 'blocked') {
          return {
            status: EnumCardItemStatus.waitingForApproval,
            tipIntent: 'danger',
          };
        }

        return {
          status: EnumCardItemStatus.approved,
          tipIntent: 'success',
        };
      })(),
    };
  });

  let cardStatus: TCardStatus = 'todo';

  if (items?.length) {
    if (items.every(item => item.tipIntent === 'success')) {
      cardStatus = 'done';
    } else if (items.some(item => item.tipIntent === 'info')) {
      cardStatus = 'review';
    } else if (hasRejectedDoc) {
      cardStatus = 'rejected';
    }
  }

  const pendingItems = items.filter(item => item.tipIntent !== 'success');

  return { items: pendingItems, cardStatus };
};

export type { TItemAction };
export { getItem };
